import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    toast.success("Message received. Our team will reply within 1–2 business days.");
  };

  return (
    <div>
      <section className="border-b border-ff-border bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-2 lg:py-20">
          <div>
            <SectionLabel>Contact</SectionLabel>
            <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-ff-blue sm:text-5xl">
              We're here to help
            </h1>
            <p className="mt-5 max-w-lg text-ff-dark">
              Questions about a campaign, a donation, or verification? Reach out and our U.S.-based support
              team will get back to you.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Mail, t: "Email", d: "help@fightfund.com" },
                { icon: MapPin, t: "Location", d: "United States (USA based)" },
                { icon: Clock, t: "Response time", d: "1–2 business days" },
              ].map((c) => (
                <div key={c.t} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F3F4F6]">
                    <c.icon className="h-5 w-5 text-ff-blue" />
                  </span>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-ff-medium">{c.t}</div>
                    <div className="font-semibold text-ff-blue">{c.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-xl border border-ff-border bg-[#F3F4F6] p-5" data-testid="contact-faq-callout">
              <p className="text-sm text-ff-dark">
                Many answers are instant — check our{" "}
                <Link to="/faq" data-testid="contact-faq-link" className="font-semibold text-ff-blue underline underline-offset-2 hover:text-ff-red">
                  frequently asked questions
                </Link>{" "}
                before reaching out.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-ff-border bg-white p-8 shadow-sm">
            {sent ? (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
                <CheckCircle2 className="mb-4 h-12 w-12 text-ff-green" />
                <h3 className="font-heading text-xl font-bold text-ff-blue">Thank you!</h3>
                <p className="mt-2 text-sm text-ff-dark">We've received your message.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="c-name">Name</Label>
                  <Input id="c-name" data-testid="contact-name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-msg">Message</Label>
                  <Textarea id="c-msg" data-testid="contact-message" required rows={5} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
                </div>
                <Button type="submit" data-testid="contact-submit" className="w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
